/**
 * @fileoverview Keyboard shortcuts hook for text editor
 * 
 * Manages keyboard shortcuts for common editor actions
 */

import { useEffect, useCallback } from 'react';
import { KEYBOARD_SHORTCUTS } from '../types.js';

/**
 * Keyboard shortcuts hook
 * 
 * @param {Object} config - Editor configuration
 * @param {Object} handlers - Action handlers
 * @returns {Object} Keyboard shortcut utilities
 */
export function useKeyboardShortcuts(config, handlers) {
  
  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback((event) => {
    if (!config.enableKeyboardShortcuts) return;

    // Check for shortcuts
    for (const [action, shortcut] of Object.entries(KEYBOARD_SHORTCUTS)) {
      if (
        event.key.toLowerCase() === shortcut.key.toLowerCase() &&
        event.ctrlKey === shortcut.ctrlKey &&
        (!shortcut.shiftKey || event.shiftKey) &&
        (!shortcut.altKey || event.altKey)
      ) {
        event.preventDefault();
        
        // Call the appropriate handler
        switch (action) {
          case 'save':
            if (handlers.onSave) handlers.onSave();
            break;
          case 'preview':
            if (handlers.onPreview) handlers.onPreview();
            break;
          case 'bold':
            if (handlers.onBold) handlers.onBold();
            break;
          case 'italic':
            if (handlers.onItalic) handlers.onItalic();
            break;
          case 'undo':
            // Let browser handle undo
            break;
          case 'redo':
            // Let browser handle redo
            break;
          default:
            break;
        }
        
        break;
      }
    }
  }, [config.enableKeyboardShortcuts, handlers]);

  /**
   * Get shortcut description for a specific action
   */
  const getShortcutDescription = useCallback((action) => {
    const shortcut = KEYBOARD_SHORTCUTS[action];
    if (!shortcut) return null;

    let description = '';
    if (shortcut.ctrlKey) description += 'Ctrl+';
    if (shortcut.shiftKey) description += 'Shift+';
    if (shortcut.altKey) description += 'Alt+';
    description += shortcut.key.toUpperCase();

    return description;
  }, []);

  /**
   * Get all available shortcuts
   */
  const getAllShortcuts = useCallback(() => {
    return Object.entries(KEYBOARD_SHORTCUTS).map(([action, shortcut]) => ({
      action,
      description: shortcut.description,
      keys: getShortcutDescription(action)
    }));
  }, [getShortcutDescription]);

  // Set up event listeners
  useEffect(() => {
    if (config.enableKeyboardShortcuts) {
      document.addEventListener('keydown', handleKeyDown);
      
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [config.enableKeyboardShortcuts, handleKeyDown]);

  return {
    getShortcutDescription,
    getAllShortcuts,
    isEnabled: config.enableKeyboardShortcuts
  };
}